<template>
  <section id="rag">
    <h4>Your RAG settings</h4>
    <div class="description">Set your RAG settings to improve the quality of the answers from your LLM.</div>

    <!-- <Toggle label="Advanced Settings " tooltip="Advance LLM settings only used for RAG" v-model="advance" /> -->

    <div class="span">
      <h4 class="mt">
        Amount of documents trained
        <Tooltip title="The amount of documents that are trained for the LLM." />
      </h4>
      <div class="form-group--inside">
        <div class="dot-wrapper">
          <div class="description trained">{{ trainedDocs }}</div>
        </div>
      </div>
    </div>

    <div class="form-group--inside">
      <div class="col-12 row m-b-20">
        <div class="col-4">
          <h4 class="mt">
            Embeddings model <Tooltip title="Set the embeddings model to use for the RAG. This defines how the text is converted into a vector." />
          </h4>
          <Dropdown :items="embeddingsModels" :value="selectedEmbeddingsModel" @input="setEmbeddingsModel($event)" />
        </div>

        <div class="col-4">
          <h4 class="mt">
            Amount of documents retrieved
            <Tooltip
              title="Set the amount of documents retrieved from the vector database. The more documents retrieved, the more context the LLM has to answer the question but this increases the time it takes to retrieve the documents."
            />
          </h4>
          <Autocomplete
            :items="variables"
            type="number"
            name="amountOfDocs"
            id="amountOfDocs"
            v-model="amountOfDocs"
            :rows="1"
            placeholder="Enter the amount of documents retrieved"
          />
        </div>
      </div>
    </div>

    <div class="row col-12">
      <div class="col-md-3 padding-right form-group">
        <h4>
          Metadata field
          <Tooltip
            title="Set the fields which are stored in the metadata of the vector store. You can use these fields to filter the documents retrieved from the vector database."
          />
        </h4>
        <input class="first-input form-control blue-card mb-16" placeholder="Enter the metadata" v-model="newFilter.metadataField" @keyup.enter="addFilter" />

        <div v-for="(filter, index) in ragFilters" :key="index">
          <input class="form-control blue-card filter-input" placeholder="Enter the metadata" v-model="filter.metadataField" />
        </div>
      </div>
      <div class="col-md-7 form-group">
        <h4>
          Filter Query
          <Tooltip
            title="Set a llm query to define the filter context for the meta data. If the model is unsure it will return NONE and not use the filter. You can use this to get the most relevant documents before passing it the LLM."
          />
        </h4>
        <input
          placeholder="Enter the LLM query to filter the documents based on the metadata field"
          v-model="newFilter.query"
          class="form-control blue-card first-input"
          @keyup.enter="addFilter"
        />
        <div type="button" class="copybutton dark" @click="addFilter()">Add</div>

        <div v-for="(filter, index) in ragFilters" :key="index">
          <input class="form-control blue-card filter-input" placeholder="Enter the metadata" v-model="filter.query" />
          <i class="icon-delete entity-delete" @click="deleteFilter(index)"></i>
        </div>
      </div>
      <div class="col-md-2 form-group">
        <h4>
          Operator
          <Tooltip title="Choose the operator for this filter. 'eq' for exact match, 'in' for matching any value in a list." />
        </h4>
        <Dropdown class="margin-bottom-10" :items="operatorOptions" title="eq" :value="newFilter.operator" @input="(value) => (newFilter.operator = value)" />
        <div v-for="(filter, index) in ragFilters" :key="index" class="operator-dropdown">
          <Dropdown :items="operatorOptions" title="eq" :value="filter.operator" @input="(value) => updateOperator(index, value)" class="operator-select" />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { Component, Prop, Mixins } from 'vue-property-decorator'
import { LLMObject, RAGFilter } from '../../../../../../common/types/llm-type'
import Autocomplete from '../../../../components/helpers/Autocomplete.vue'
import { LlmDocumentType } from '../../../../../../common/enums/bot/llm/llm-document-type.enum'
import { LLM_SUPPORTED_FILETYPES } from '../../../../../../common/constants/llm-document.constants'
import popupService from '@/services/popup.service'
import LlmManagementMixin from '../../bots/_mixins/llm-management.mixin'
import { OpenAIEmbeddingsModelsEnabled } from '@common/enums/open-ai-models'
import { NomicEmbeddingsModelsEnabled } from '@common/enums/nomic/embeddings-model.enum'
import Dropdown from '@/components/helpers/Dropdown.vue'
import Tooltip from '@/components/helpers/Tooltip.vue'
import Toggle from '@/components/helpers/Toggle.vue'

@Component({ components: { Autocomplete, Dropdown, Tooltip, Toggle } })
export default class LLMRag extends Mixins(LlmManagementMixin) {
  @Prop() current: LLMObject
  @Prop() variables: string[]
  @Prop() docError: string

  openAiEmbeddingsModels = OpenAIEmbeddingsModelsEnabled
  nomicEmbeddingsModels = NomicEmbeddingsModelsEnabled
  newFilter = {
    query: '',
    metadataField: '',
    operator: 'eq'
  }

  selectedEmbeddingsModel: typeof OpenAIEmbeddingsModelsEnabled | typeof NomicEmbeddingsModelsEnabled | string = 'default'

  operatorOptions = ['eq', 'in']

  get embeddingsModels() {
    return ['default', ...Object.values(OpenAIEmbeddingsModelsEnabled), ...Object.values(NomicEmbeddingsModelsEnabled)]
  }

  get LlmDocumentType() {
    return LlmDocumentType
  }

  get amountOfDocs() {
    return this.current.amountOfDocs + ''
  }

  set amountOfDocs(value: string) {
    this.current.amountOfDocs = Number(value)
  }

  get metadataFields() {
    return this.current.metadataFields
  }

  set metadataFields(_value: string[]) {
    return
  }

  get trainedDocs() {
    return this.current.trainedDocs || 0
  }

  get ragFilters(): RAGFilter[] {
    return this.current.ragFilters || []
  }

  set ragFilters(_value: string[]) {
    return
  }

  mounted() {
    this.selectedEmbeddingsModel = this.current.embeddingModel
    console.log(this.current)
  }

  async addFilter() {
    if (this.newFilter.metadataField && this.newFilter.query) {
      this.ragFilters.push({
        metadataField: this.newFilter.metadataField,
        query: this.newFilter.query,
        operator: this.newFilter.operator
      })

      this.newFilter.metadataField = ''
      this.newFilter.query = ''
      this.newFilter.operator = 'eq'
    }
  }

  async deleteFilter(index: number) {
    const answer = await popupService.showDialog('Are you sure you want to delete this filter?', 'You will not be able to bring it back later')

    if (answer) {
      this.current.ragFilters.splice(index, 1)
      this.$emit('save')
    }
  }

  setEmbeddingsModel(model: 'default' | typeof OpenAIEmbeddingsModelsEnabled | typeof NomicEmbeddingsModelsEnabled) {
    this.current.embeddingModel = model
    this.selectedEmbeddingsModel = model
    popupService.showInfo(`New embeddings model set. Please retrain your LLM to apply the changes else the LLM won't work anymore.`)
  }

  llmSupportedFiletypes() {
    return LLM_SUPPORTED_FILETYPES
  }

  updateOperator(index: number, value: string) {
    console.log(index, value)
    this.ragFilters[index].operator = value
  }
}
</script>

<style scoped lang="scss">
@import '../../../../assets/scss/variables';

.filter-input {
  background-color: $light-blue-bg !important;
  color: $default-dark !important;
  border: none !important;
  margin-bottom: 10px !important;
}

.icon-delete {
  top: -26px !important;
}

.entity-delete {
  margin-top: -10px !important;
}

.mb-16 {
  margin-bottom: 16px;
}

.description {
  font-size: 14px;
  margin-bottom: 15px;
}

.empty {
  color: $grey;
  font-size: 12px;
}

.m-b-5 {
  margin-bottom: 5px;
}

.m-b-20 {
  margin-bottom: 20px;
}

.documents {
  background-color: $blue-background;
  padding: 10px 5px 10px 15px;
  border-radius: 12px;
  font-weight: 500;
  color: $default-dark;

  .entity-delete {
    margin-left: 5px;
  }
}

.operator-dropdown {
  margin-bottom: 10px;
  height: 43px;
}

.margin-bottom-10 {
  margin-bottom: 16px;
}

.operator-select {
  width: 100%;
}
</style>
